import React from 'react';

/**
 * The suite builder's layout model: ordered `layers` of workflow ids plus a
 * `staging` list (in the suite but not in any layer — excluded from runs).
 */
export interface Arrangement {
  layers: string[][];
  staging: string[];
}

const idListsEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/** Structural equality over an arrangement (order-sensitive). */
export function arrangementsEqual(a: Arrangement, b: Arrangement): boolean {
  return (
    idListsEqual(a.staging, b.staging) &&
    a.layers.length === b.layers.length &&
    a.layers.every((layer, i) => idListsEqual(layer, b.layers[i]))
  );
}

const EMPTY: Arrangement = { layers: [[]], staging: [] };

/** Cap the undo stack so a long editing session can't grow memory unbounded. */
const MAX_UNDO = 50;

/**
 * Local edit history for the suite builder's {@link Arrangement}. Every layout
 * mutation goes through `apply`, which pushes the prior state onto an undo
 * stack; `undo` pops it. `dirty` compares the present against the saved
 * baseline, so undoing all the way back to the loaded state re-disables Save.
 *
 * Scope is layout only (layers + staging) — suite name / failure-mode edits are
 * tracked separately by the page.
 */
export function useArrangementHistory() {
  const [present, setPresent] = React.useState<Arrangement>(EMPTY);
  const [past, setPast] = React.useState<Arrangement[]>([]);
  const [baseline, setBaseline] = React.useState<Arrangement>(EMPTY);

  /** Load a fresh arrangement (suite loaded / refetched): clears history + baseline. */
  const reset = React.useCallback((next: Arrangement) => {
    setPresent((prev) => (arrangementsEqual(prev, next) ? prev : next));
    setPast((prev) => (prev.length === 0 ? prev : []));
    setBaseline((prev) => (arrangementsEqual(prev, next) ? prev : next));
  }, []);

  /** Apply a layout edit, pushing the prior state onto the undo stack. No-op if unchanged. */
  const apply = React.useCallback(
    (updater: Arrangement | ((prev: Arrangement) => Arrangement)) => {
      setPresent((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (arrangementsEqual(prev, next)) return prev;
        // Cap the undo stack so a long editing session can't grow it unbounded.
        setPast((p) => [...p, prev].slice(-MAX_UNDO));
        return next;
      });
    },
    []
  );

  const undo = React.useCallback(() => {
    setPast((p) => {
      if (p.length === 0) return p;
      setPresent(p[p.length - 1]);
      return p.slice(0, -1);
    });
  }, []);

  /** Mark the present as the saved baseline (after a successful save). */
  const markSaved = React.useCallback(() => {
    setBaseline(present);
    setPast([]);
  }, [present]);

  const canUndo = past.length > 0;
  const dirty = !arrangementsEqual(present, baseline);

  return { present, apply, undo, canUndo, dirty, reset, markSaved };
}
